import React, {useMemo, useState} from "react";
import {Button, Stack, Typography} from "@mui/material";
import {useFormik} from "formik";
import * as Yup from "yup";
import {useMutation} from "@tanstack/react-query";
import {DefaultError} from "@tanstack/query-core";
import {enqueueSnackbar} from "notistack";
import {ModalWindow} from "../../modal/ModalWindow";
import {TextInput} from "../../form/TextInput";
import {User} from "../../../types/User";
import {updateUser} from "../../../service/user.service";
import {useAuthStore} from "../../../store/auth.store";
import {TelegramInstruction} from "./TelegramInstruction";

interface TelegramChatModalProps {
    user: User;
    onClose: () => void;
    onSuccess?: (user: User) => void;
}

interface TelegramChatForm {
    telegramChatId: string;
    telegramChatOwner: string;
}

export function TelegramChatModal({user, onClose, onSuccess}: TelegramChatModalProps) {
    const authStore = useAuthStore();
    const [submitHappen, setSubmitHappen] = useState(false);

    const validationSchema = useMemo(() => Yup.object({
        telegramChatId: Yup.string()
            .trim()
            .required("Telegram chat id is required"),
        telegramChatOwner: Yup.string()
            .trim()
    }), []);

    const formik = useFormik<TelegramChatForm>({
        initialValues: {
            telegramChatId: user.telegramChatId || "",
            telegramChatOwner: user.telegramChatOwner || ""
        },
        validationSchema,
        onSubmit: (values) => updateMutation.mutate(values)
    });

    const updateMutation = useMutation<User, DefaultError, TelegramChatForm>({
        mutationFn: (values) => updateUser({
            ...user,
            telegramChatId: values.telegramChatId.trim(),
            telegramChatOwner: values.telegramChatOwner.trim()
        }).then(r => r.data),
        onSuccess: (updatedUser) => {
            authStore.setUser(updatedUser);
            enqueueSnackbar({message: "Telegram target settings updated", variant: "success"});
            onSuccess?.(updatedUser);
            onClose();
        },
        onError: (error) => {
            enqueueSnackbar({message: "Failed to update Telegram target settings: " + error.message, variant: "error"});
        }
    });

    function submit() {
        setSubmitHappen(true);
        formik.submitForm().catch(() => void 0);
    }

    return <ModalWindow
        width={500}
        loading={updateMutation.isPending}
        title={"Telegram target settings"}
        onClose={onClose}
        onEnter={submit}
    >
        <form onSubmit={(e) => {
            e.preventDefault();
            submit();
        }}>
            <Stack gap={"15px"} sx={{paddingTop: '5px'}}>
                <TelegramInstruction />
                <Stack gap={"5px"}>
                    <TextInput
                        fullWidth={true}
                        label={"Telegram group/channel id"}
                        name={"telegramChatId"}
                        submitHappen={submitHappen}
                        formik={formik}
                    />
                    <Typography variant={"body2"} color={"textSecondary"}>
                        Chat ID can be a group, supergroup, or channel id. It often looks like -1003850946498.
                    </Typography>
                </Stack>
                <Stack gap={"5px"}>
                    <TextInput
                        fullWidth={true}
                        label={"Contact username in post (optional)"}
                        name={"telegramChatOwner"}
                        submitHappen={submitHappen}
                        formik={formik}
                    />
                    <Typography variant={"body2"} color={"textSecondary"}>
                        Optional. If provided, it will be shown in the post as the contact, for example @yournickname.
                    </Typography>
                </Stack>
                <Stack flexDirection={"row"} gap={"10px"} justifyContent={"flex-end"}>
                    <Button color={"inherit"} onClick={onClose}>Cancel</Button>
                    <Button type={"submit"} variant={"contained"}>Save</Button>
                </Stack>
            </Stack>
        </form>
    </ModalWindow>
}
